import { IRootScopeService, ILogService, IPromise, IQService, IDeferred } from "angular";
import ReconnectingWebSocket from "reconnecting-websocket";

import { EnvironmentService } from "@gtmhub/env";
import { getToken } from "@gtmhub/auth";
import { AccountResolverService } from "../state/accountResolverService";
import { PlanningSessionsActions } from "@gtmhub/sessions";
import { INgRedux } from "@gtmhub/state-management";
import { AssigneeActions } from "../assignees/redux/assignee-actions";
import { storage } from "@gtmhub/core/storage";
import { IGoalsMetricsData, INotificationsCountData, IInsightCalculationData, ISocketV2Data, IEntityData } from "./models";
import { IAssignee } from "@gtmhub/assignees/models";
import { ICustomFieldWSData, CustomFieldActions } from "@gtmhub/customFields";
import { IUser } from "@gtmhub/users";
import { ITeam, TeamsActions } from "@gtmhub/teams";
import { CurrentEmployeeActions, IEmployee } from "@gtmhub/employees";
import { applySocketUpdatesToIdMap } from "./util";
import { IdMap } from "@gtmhub/util";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { BadgesActions } from "@gtmhub/badges/redux/badges-actions";
import { IBadge } from "@gtmhub/badges";

// untyped messages
interface IGeneralMessage {
  messageType:
    | "entityCreated"
    | "entityStatusChanged"
    | "dataFailuresRecalculated"
    | "dataSourceCreated"
    | "dataSourceStatusChanged"
    | "accountSessionsUpdated"
    | "insightStateUpdated"
    | "goalDesignScore"
    | "entitySyncProgress"
    | "subscriptionConverted"
    | "entitiesDeletedParentEntitiesChanged";
  data: unknown;
}

interface IGoalsAndMetricsMessage {
  messageType: "goalAndMetrics";
  data: IGoalsMetricsData;
}

interface IAccountAssigneesUpdatedV2Message {
  messageType: "accountAssigneesUpdatedV2";
  data: ISocketV2Data<IAssignee>;
}

interface IAccountUsersUpdatedV2Message {
  messageType: "accountUsersUpdatedV2";
  data: ISocketV2Data<IUser>;
}

interface IAccountTeamsUpdatedV2Message {
  messageType: "accountTeamsUpdatedV2";
  data: ISocketV2Data<ITeam>;
}

interface IAccountBadgesUpdatedMessage {
  messageType: "accountBadgesUpdated";
  data: IBadge[];
}

interface IAccountCustomFieldsUpdatedMessage {
  messageType: "accountCustomFieldsUpdated";
  data: ICustomFieldWSData;
}

interface INotificationsCountMessage {
  messageType: "notificationsCount";
  data: INotificationsCountData;
}

export interface IInsightCalculationResultMessage {
  messageType: "insightResponse";
  data: IInsightCalculationData;
  transactionID: string;
}

export interface IEntityDataMessage {
  messageType: "entityDataResponse";
  data: IEntityData;
}

type IMessage =
  | IGoalsAndMetricsMessage
  | IAccountAssigneesUpdatedV2Message
  | IAccountUsersUpdatedV2Message
  | IAccountTeamsUpdatedV2Message
  | INotificationsCountMessage
  | IGeneralMessage
  | IInsightCalculationResultMessage
  | IAccountCustomFieldsUpdatedMessage
  | IEntityDataMessage
  | IAccountBadgesUpdatedMessage;

const handleUnknownMessage = (message: never) => console.warn("Unknown web socket message", message);

export class MessagingService {
  public static $inject = [
    "$rootScope",
    "$log",
    "$ngRedux",
    "$q",
    "EnvironmentService",
    "AccountResolverService",
    "PlanningSessionsActions",
    "AssigneeActions",
    "TeamsActions",
    "BadgesActions",
    "CustomFieldActions",
    "CurrentEmployeeActions",
  ];

  constructor(
    private $rootScope: IRootScopeService & ITraceRootScopeService,
    private $log: ILogService,
    private $ngRedux: INgRedux,
    private $q: IQService,
    private environmentService: EnvironmentService,
    private accountResolverService: AccountResolverService,
    private planningSessionActions: PlanningSessionsActions,
    private assigneeActions: AssigneeActions,
    private teamsActions: TeamsActions,
    private badgesActions: BadgesActions,
    private customFieldActions: CustomFieldActions,
    private currentEmployeeActions: CurrentEmployeeActions
  ) {
    this.$log.info("Initialized messaging service.");
    this.insightsSocketPromise = this.$q.defer();
  }

  private socket: ReconnectingWebSocket;
  private insightsSocket: ReconnectingWebSocket;
  private insightsSocketPromise: IDeferred<unknown>;
  private accountId: string;

  private readonly connectionTimeoutInMs = 10000;

  public init(): void {
    this.accountId = this.accountResolverService.getAccountId();
    if (!this.accountId) {
      return;
    }
    this.initializeSocket();
    this.initializeInsightsSocket();
  }

  public onInsightsSocketOpen(): IPromise<unknown> {
    if (!this.accountId) {
      this.insightsSocketPromise.reject("missing accountId");
      return this.insightsSocketPromise.promise;
    }
    if (!this.insightsSocket) {
      this.initializeInsightsSocket();
    }
    return this.insightsSocketPromise.promise;
  }

  private initializeSocket(): void {
    const token = getToken();
    const url = this.environmentService.getWSEndpoint(`/subscribe/${this.accountId}?authorization=${token}`);

    this.socket = new ReconnectingWebSocket(url, null, { connectionTimeout: this.connectionTimeoutInMs });
    this.socket.onmessage = (event: MessageEvent) => this.socketOnMessage(event);
  }

  private initializeInsightsSocket(): void {
    const token = getToken();
    const url = this.environmentService.getInsightsSocketsEndpoint(`/subscribe?authorization=${token}&accountId=${this.accountId}`);

    this.insightsSocket = new ReconnectingWebSocket(url, null, { connectionTimeout: this.connectionTimeoutInMs });
    this.insightsSocket.onmessage = (event: MessageEvent) => this.socketOnMessage(event);
    this.insightsSocket.onopen = () => this.insightsSocketPromise.resolve(ReconnectingWebSocket.OPEN);
    this.insightsSocket.onclose = () => {
      // Build a promise chain so that all deferred promises are resolved on 'onopen' event (not only the last one).
      const oldPromise = this.insightsSocketPromise;
      const newPromise = this.$q.defer();
      newPromise.promise.then(() => oldPromise.resolve());
      this.insightsSocketPromise = newPromise;
    };
  }

  private socketOnMessage(event: MessageEvent): void {
    const userId: string = storage.get("userId");
    const message: IMessage = JSON.parse(event.data);
    switch (message.messageType) {
      case "entityCreated":
        this.$rootScope.$broadcast("entityCreated", message.data);
        break;
      case "entityStatusChanged":
        this.$rootScope.$apply(() => this.$rootScope.$broadcast("entityStatusChanged", message.data));
        break;
      case "entityDataResponse":
        this.$rootScope.$apply(() => this.$rootScope.$broadcast("entityDataResponse", message.data));
        break;
      case "dataFailuresRecalculated":
        this.$rootScope.$broadcast("dataFailuresRecalculated", message.data);
        break;
      case "dataSourceCreated":
        this.$rootScope.$broadcast("dataSourceCreated", message.data);
        break;
      case "dataSourceStatusChanged":
        this.$rootScope.$apply(() => this.$rootScope.$broadcast("dataSourceStatusChanged", message.data));
        break;
      case "insightResponse":
        this.$rootScope.$apply(() => this.$rootScope.$broadcast("insightResponse", message));
        break;
      case "accountSessionsUpdated":
        this.$rootScope.traceAction("sessions_updated", () => {
          this.$ngRedux.dispatch(this.planningSessionActions.getSessions());
        });
        break;
      case "accountUsersUpdatedV2":
        {
          this.$rootScope.$broadcast("accountUsersUpdatedV2", message.data);

          const usersUpdatesIdMap = applySocketUpdatesToIdMap(<IdMap<IUser>>{}, message.data);
          if (usersUpdatesIdMap[userId]) {
            const { email, firstName, lastName } = usersUpdatesIdMap[userId];
            const updatedEmployee: Partial<IEmployee> = {
              email,
              firstName,
              lastName,
            };
            this.$ngRedux.dispatch(this.currentEmployeeActions.updateCurrentEmployee(updatedEmployee));
          }
        }
        break;
      case "accountAssigneesUpdatedV2":
        this.$ngRedux.dispatch(this.assigneeActions.updateAssignees(message.data));
        break;
      case "accountTeamsUpdatedV2":
        {
          this.$ngRedux.dispatch(this.teamsActions.updateTeams(message.data));
          this.$ngRedux.dispatch(this.currentEmployeeActions.updateCurrentEmployeeTeams(message.data));
          this.$rootScope.$broadcast("accountTeamsUpdatedV2", message.data);
        }
        break;
      case "accountBadgesUpdated":
        this.$rootScope.$broadcast("accountBadgesUpdated", message.data);
        this.$ngRedux.dispatch(this.badgesActions.updateBadges(message.data));
        break;
      case "notificationsCount":
        {
          if (userId === message.data.userID) {
            this.$rootScope.$broadcast("notificationsCount", message.data);
          }
        }
        break;
      case "goalAndMetrics":
        this.$rootScope.$apply(() => this.$rootScope.$broadcast("wsGoalsAndMetricsChanged", message.data));
        break;
      case "insightStateUpdated":
        this.$rootScope.$broadcast("insightStateUpdated", message);
        break;
      case "accountCustomFieldsUpdated":
        this.$ngRedux.dispatch(this.customFieldActions.updateCustomFields(message.data));
        this.$rootScope.$broadcast("accountCustomFieldsUpdated", message.data);
        break;
      case "goalDesignScore":
        this.$rootScope.$broadcast("updateGoalDesignScore", message.data);
        break;
      case "entitySyncProgress":
        this.$rootScope.$apply(() => this.$rootScope.$broadcast("entitySyncProgress", message.data));
        break;
      case "subscriptionConverted":
        this.$rootScope.$broadcast("subscriptionConverted", message.data);
        break;
      case "entitiesDeletedParentEntitiesChanged":
        this.$rootScope.$apply(() => this.$rootScope.$broadcast("entitiesDeletedParentEntitiesChanged", message.data));
        break;
      default:
        handleUnknownMessage(message);
        break;
    }
  }
}
